import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  addDays,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subWeeks,
  addMonths,
  subMonths,
} from "date-fns";
import { EventCategory } from "@vedaunion/shared";
import { useEvents, useUpcomingEvents, calendarFeedIcsHref } from "../../lib/events";
import { useCan } from "../../lib/auth";
import { CalendarView } from "../../components/calendar/CalendarView";
import { WeekView } from "../../components/calendar/WeekView";
import { AgendaView } from "../../components/calendar/AgendaView";

/**
 * Dashboard calendar — three views (Month / Week / Agenda) sharing the same
 * filter chip rail. URL-backed view state so refresh / share preserves the
 * user's frame of reference. Click any empty area in Month or Week opens the
 * create form pre-filled with that day/time.
 *
 * Month view uses CalendarView (shared with public calendar) for the panchāṅga
 * overlay + location picker. Week and Agenda stay as-is.
 *
 * Editors with `events.edit_any` see drafts and private events too (via
 * `?all=true` on the underlying query).
 */

type View = "month" | "week" | "agenda";

const CATEGORY_OPTIONS = EventCategory.options;

export default function DashboardCalendar() {
  const navigate = useNavigate();
  const canCreate = useCan("events.create");
  const canEditAny = useCan("events.edit_any");
  const [params, setParams] = useSearchParams();

  const view: View = (() => {
    const v = params.get("view");
    return v === "week" || v === "agenda" ? v : "month";
  })();

  // Cursor — drives Month and Week views. Stored in URL as `?d=YYYY-MM-DD` so
  // navigation persists; defaults to today when unset.
  const cursorParam = params.get("d");
  const [cursor, setCursor] = useState<Date>(() => {
    if (cursorParam) {
      const d = new Date(cursorParam);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  });
  useEffect(() => {
    if (cursorParam) {
      const d = new Date(cursorParam);
      if (!Number.isNaN(d.getTime()) && d.toISOString() !== cursor.toISOString()) setCursor(d);
    }
  }, [cursorParam]);
  const setCursorAndUrl = (next: Date) => {
    setCursor(next);
    const sp = new URLSearchParams(params);
    sp.set("d", format(next, "yyyy-MM-dd"));
    setParams(sp, { replace: true });
  };

  // Filter chips
  const category = params.get("category") ?? "";
  const onlyMine = params.get("mine") === "1";
  const setParam = (key: string, value: string | null) => {
    const sp = new URLSearchParams(params);
    if (value === null || value === "") sp.delete(key);
    else sp.set(key, value);
    setParams(sp, { replace: true });
  };

  // -- Range queries based on the active view --
  const monthRange = useMemo(() => ({
    from: format(startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), "yyyy-MM-dd"),
    to: format(addDays(endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }), 1), "yyyy-MM-dd"),
  }), [cursor]);
  const weekRange = useMemo(() => ({
    from: format(startOfWeek(cursor, { weekStartsOn: 1 }), "yyyy-MM-dd"),
    to: format(addDays(startOfWeek(cursor, { weekStartsOn: 1 }), 7), "yyyy-MM-dd"),
  }), [cursor]);

  const monthQ = useEvents({
    from: monthRange.from,
    to: monthRange.to,
    all: canEditAny || undefined,
    category: category || undefined,
    limit: 500,
  });
  const weekQ = useEvents({
    from: weekRange.from,
    to: weekRange.to,
    all: canEditAny || undefined,
    category: category || undefined,
    limit: 200,
  });
  const agendaQ = useUpcomingEvents({ days: 60, limit: 50, category: category || undefined });

  const filteredMonth = (monthQ.data?.events ?? []).filter((e) => (onlyMine ? !!e.myRsvp : true));
  const filteredWeek = (weekQ.data?.events ?? []).filter((e) => (onlyMine ? !!e.myRsvp : true));
  const filteredAgenda = (agendaQ.data?.events ?? []).filter((e) => (onlyMine ? !!e.myRsvp : true));

  const onCreateAt = (when: Date) => {
    const sp = new URLSearchParams();
    sp.set("when", when.toISOString());
    navigate(`/dashboard/calendar/new?${sp.toString()}`);
  };

  const onSetView = (v: View) => {
    const sp = new URLSearchParams(params);
    if (v === "month") sp.delete("view");
    else sp.set("view", v);
    setParams(sp, { replace: true });
  };

  // Period title shown beside the nav buttons (replaces the duplicated header
  // each grid component used to render). Empty for the agenda view.
  const periodLabel = (() => {
    if (view === "month") return format(cursor, "MMMM yyyy");
    if (view === "week") {
      const weekStart = startOfWeek(cursor, { weekStartsOn: 1 });
      return `${format(weekStart, "d LLL")} — ${format(addDays(weekStart, 6), "d LLL yyyy")}`;
    }
    return "";
  })();

  return (
    <div className="cal-shell">
      <header className="cal-shell-head">
        <div>
          <p className="page-eyebrow">Calendar</p>
          <h1 className="page-title">Events &amp; gatherings.</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <a className="row-action" href={calendarFeedIcsHref()} title="Subscribe in Apple/Google/Outlook calendar">
            Subscribe (.ics)
          </a>
          {canCreate && (
            <Link to="/dashboard/calendar/new" className="btn-app">
              New event
            </Link>
          )}
        </div>
      </header>

      {/* Combined view tabs + period title + nav buttons + filter chips. One
          dense row on desktop; wraps on narrower widths. */}
      <div className="cal-controls">
        <nav className="cal-tabs" aria-label="Calendar views">
          {(["month", "week", "agenda"] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`subtab ${view === v ? "is-active" : ""}`}
              onClick={() => onSetView(v)}
            >
              {v === "agenda" ? "Agenda" : v === "week" ? "Week" : "Month"}
            </button>
          ))}
        </nav>

        {periodLabel && (
          <div className="cal-period">
            <button
              type="button"
              className="tt-btn cal-period-nav"
              onClick={() => setCursorAndUrl(view === "week" ? subWeeks(cursor, 1) : subMonths(cursor, 1))}
              aria-label="Previous"
            >
              ‹
            </button>
            <span className="cal-period-label">{periodLabel}</span>
            <button
              type="button"
              className="tt-btn cal-period-nav"
              onClick={() => setCursorAndUrl(view === "week" ? addWeeks(cursor, 1) : addMonths(cursor, 1))}
              aria-label="Next"
            >
              ›
            </button>
            <button type="button" className="tt-btn cal-period-today" onClick={() => setCursorAndUrl(new Date())}>
              Today
            </button>
          </div>
        )}

        <div className="cal-filters">
          <label className={`chip chip-select ${category ? "is-active" : ""}`}>
            <span className="chip-label">Category</span>
            <select value={category} onChange={(e) => setParam("category", e.target.value)} aria-label="Category filter">
              <option value="">all</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`chip ${onlyMine ? "is-active" : ""}`}
            onClick={() => setParam("mine", onlyMine ? null : "1")}
          >
            Only my RSVPs
          </button>
          {(category || onlyMine) && (
            <button
              type="button"
              className="chip chip-clear"
              onClick={() => {
                const sp = new URLSearchParams(params);
                sp.delete("category");
                sp.delete("mine");
                setParams(sp, { replace: true });
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Active view — fills the remaining viewport height in month/week so a
          typical laptop screen shows the whole grid without scrolling. */}
      <div className="cal-viewport">
        {view === "month" && (
          <CalendarView
            register="functional"
            cursor={cursor}
            onCursorChange={setCursorAndUrl}
            events={filteredMonth}
            basePath="/dashboard/calendar"
            showHeader={false}
            onCreateAt={canCreate ? onCreateAt : undefined}
          />
        )}
        {view === "week" && (
          <WeekView
            cursor={cursor}
            events={filteredWeek}
            onCursorChange={setCursorAndUrl}
            basePath="/dashboard/calendar"
            onCreateAt={canCreate ? onCreateAt : undefined}
            showHeader={false}
          />
        )}
        {view === "agenda" && (
          <AgendaView
            events={filteredAgenda}
            basePath="/dashboard/calendar"
            onCreateAt={canCreate ? onCreateAt : undefined}
          />
        )}
      </div>
    </div>
  );
}
